import I18n from 'I18n';
import { LogEventTypes, NotificationTypes } from 'automation-ui-log-events/constants/LogEventConstants';
import { ActionOptions } from 'automation-ui-log-events/constants/ActionConstants';
import { HISTORY_ACTION_TYPES as HistoryActionTypes } from 'automation-ui-log-events/api-client/constants/HistoryActionTypes';
import { toDateStringFromSimpleDate } from 'automation-ui-management/utils/dateRangeUtils';
import { ALL_PRESET_ID, CUSTOM_PRESET_ID, DATE_RANGE_PRESETS } from 'automation-ui-management/constants/DateRangePickerOptions';
import { DefaultApplicationHistoryFilterProps } from 'automation-ui-management/records/ApplicationHistoryFilters/ApplicationHistoryFilters';
import { ApplicationHistoryFilterKeys } from 'automation-ui-management/constants/ApplicationHistoryFilterKeys';
import { Timezones } from 'automation-ui-management/constants/DateConstants';
import { toSimpleDate } from 'automation-ui-management/utils/dateRangeUtils';
import { isValid, toMomentPortalTz } from 'foundations-components/transitional/utils/SimpleDate';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
export const validateNotificationType = propMap => {
  const hasValidNotificationType = Object.keys(NotificationTypes).includes(propMap.get(ApplicationHistoryFilterKeys.NOTIFICATION_TYPE));
  if (!hasValidNotificationType) {
    return propMap.set(ApplicationHistoryFilterKeys.NOTIFICATION_TYPE, NotificationTypes.ALL);
  }
  return propMap;
};
export const hasFiltersApplied = filters => Object.values(ApplicationHistoryFilterKeys).some(key => filters[key] !== DefaultApplicationHistoryFilterProps[key]);

// Returns the revision date range in the format of startDate and endDate
// to be used to set the bounds on the date range selector. Time is dropped here
// as the date range selector can only display day/month/year
export const getRevisionDateRange = filters => {
  const startTimestamp = filters.getRevisionStartTimestamp();
  const endTimestamp = filters.getRevisionEndTimestamp();
  return {
    presetId: CUSTOM_PRESET_ID,
    startDate: startTimestamp ? toSimpleDate(startTimestamp) : undefined,
    endDate: endTimestamp ? toSimpleDate(endTimestamp) : undefined
  };
};
// Used to get the START_DATE and END_DATE filters for setting the dates
// on the record when the date range changes via the date selector.
export const getHistoryFiltersFromDateRange = ({
  presetId,
  startDate,
  endDate
}) => {
  return {
    [ApplicationHistoryFilterKeys.RANGE_TYPE]: presetId,
    [ApplicationHistoryFilterKeys.START_DATE]: presetId === CUSTOM_PRESET_ID ? toDateStringFromSimpleDate(startDate) : null,
    [ApplicationHistoryFilterKeys.END_DATE]: presetId === CUSTOM_PRESET_ID ? toDateStringFromSimpleDate(endDate) : null
  };
};

// Used to determine what timestamps to send to the UE Applications History API
export const getTimestampsFromHistoryFilters = filters => {
  const rangeType = filters.getRangeType();
  if (!rangeType) return null;
  const dateRangeOption = DATE_RANGE_PRESETS[rangeType];
  if (dateRangeOption) {
    const {
      startDate,
      endDate
    } = dateRangeOption.getValue(Timezones.PORTAL_TZ);
    return {
      startTimestamp: toMomentPortalTz(startDate).valueOf(),
      endTimestamp: toMomentPortalTz(endDate).add(1, 'days').valueOf()
    };
  } else if (rangeType === CUSTOM_PRESET_ID) {
    const revisionStartTimestamp = filters.getRevisionStartTimestamp();
    const revisionEndTimestamp = filters.getRevisionEndTimestamp();
    let startTimestamp = filters.getStartDate() ? I18n.moment(filters.getStartDate()).valueOf() : undefined;
    let endTimestamp = filters.getEndDate() ? I18n.moment(filters.getEndDate()).add(1, 'days').valueOf() : undefined;

    // Limit the to the timestamp of the revision
    if (revisionStartTimestamp) {
      startTimestamp = !startTimestamp || revisionStartTimestamp > startTimestamp ? revisionStartTimestamp : startTimestamp;
    }
    if (revisionEndTimestamp) {
      endTimestamp = !endTimestamp || revisionEndTimestamp < endTimestamp ? revisionEndTimestamp : endTimestamp;
    }
    return {
      startTimestamp,
      endTimestamp
    };
  } else {
    return null;
  }
};

// Used to determine what date range to show on the date range filter
export const makeDateRangeFromFilters = filters => {
  const rangeType = filters.getRangeType();
  const startDate = toSimpleDate(filters.getStartDate());
  const endDate = toSimpleDate(filters.getEndDate());
  if (rangeType === CUSTOM_PRESET_ID) {
    return {
      presetId: rangeType,
      startDate: isValid(startDate) ? startDate : undefined,
      endDate: isValid(endDate) ? endDate : undefined
    };
  } else if (DATE_RANGE_PRESETS[rangeType]) {
    return Object.assign({
      presetId: rangeType
    }, DATE_RANGE_PRESETS[rangeType].getValue(Timezones.PORTAL_TZ));
  }
  return {
    presetId: ALL_PRESET_ID,
    startDate: undefined,
    endDate: undefined
  };
};
export const getDefaultedApplicationFilters = updates => {
  const wasUpdatedToDefault = key => {
    return updates[key] === DefaultApplicationHistoryFilterProps[key];
  };
  return Object.keys(updates).filter(wasUpdatedToDefault);
};

/* Params functions for API params */
const getObjectIdParams = (objectId, objectTypeId) => {
  if (objectId && objectTypeId) {
    return {
      objectId,
      objectTypeId
    };
  }
  return null;
};
const getActionTypeIdParams = (actionTypeId, associatedActionTypeIds) => {
  if (!actionTypeId) {
    return null;
  }
  if (actionTypeId === HistoryActionTypes.ALL) {
    // Intentionally blank; BE sends all when nothing specified
    return null;
  } else if (actionTypeId === HistoryActionTypes.ENROLLMENT) {
    return {
      eventTypes: [LogEventTypes.ENROLLMENT, LogEventTypes.PRE_ENROLLMENT, LogEventTypes.UNENROLLMENT]
    };
    // Dev note (SE): Custom Code and Webhook actions have unique actionTypeIds
    // perinstance, which causes issues when trying to filter by action type. To
    // work around this, we are passing the actionType param instead. The BE
    // team is planning to implement a more robust solution in the future, and
    // when that is complete this workaround should be removed.
    //
    // https://hubspot.slack.com/archives/C01B1N75J5C/p1652819702497129
    // https://issues.hubspotcentral.com/browse/WORKFLOWS-10912
  } else if (actionTypeId === ActionTypes.CUSTOM_CODE) {
    return {
      eventTypes: [LogEventTypes.ACTION_EXECUTION],
      actionType: ActionTypes.CUSTOM_CODE
    };
  } else if (actionTypeId === ActionTypes.WEBHOOK) {
    return {
      eventTypes: [LogEventTypes.ACTION_EXECUTION],
      actionType: ActionTypes.WEBHOOK
    };
  } else {
    return {
      eventTypes: [LogEventTypes.ACTION_EXECUTION],
      actionTypeIds: associatedActionTypeIds && associatedActionTypeIds.length > 0 ? associatedActionTypeIds : [actionTypeId]
    };
  }
};
const getNotificationTypeParam = notificationType => {
  if (notificationType === NotificationTypes.ALL) {
    return null;
  }
  return {
    notificationType
  };
};
const getActionIdParams = actionId => {
  if (actionId === ActionOptions.ALL) {
    return null;
  }
  return {
    actionExecutionId: actionId
  };
};
export const getApplicationRequestQueryParams = (filters, objectTypeId, associatedActionTypeIds = []) => {
  return Object.assign({}, getActionIdParams(filters.getActionId()), getTimestampsFromHistoryFilters(filters), getActionTypeIdParams(filters.getActionTypeId(), associatedActionTypeIds), getNotificationTypeParam(filters.getNotificationType()), getObjectIdParams(filters.getObjectId(), objectTypeId));
};